import { Injectable } from '@angular/core';
import { highlightText, Quiz } from 'funnel/src/app/data/quiz';
import {
  FunnelFlowService,
  QuizStepKey,
} from '../../../../funnel/src/app/services/funnel-flow.service';
import { FunnelFlowConfig } from 'funnel/src/app/flow/flow-config';
import { applovinFunnelFlow } from '../applovin-funnel.flow';

declare const $localize: any;

/**
 * AppLovin-compliant funnel flow service.
 *
 * This service overrides specific steps to ensure compliance with AppLovin ad network
 * requirements, including:
 * - Adding disclaimers to statistical claims
 * - Using research-backed citations
 * - Softening predictive language
 * - Avoiding unsubstantiated superlative claims
 */
@Injectable({
  providedIn: 'root',
})
export class ApplovinFunnelFlowService extends FunnelFlowService {
  protected override getFlowConfig(): FunnelFlowConfig {
    return applovinFunnelFlow;
  }

  override customizeStepData(key: QuizStepKey): Quiz | undefined {
    const data = super.customizeStepData(key) || this.steps[key];

    switch (key) {
      case 'audioExperienceInfo':
        return {
          ...data,
          subtitle: $localize`:@@applovinAudioExpInfo:Peech is an advanced audio productivity app trusted by millions.\n\nOur powerful AI turns anything you read into audiobook - with human-sounding voices adapted based on your profile.`,
        };

      case 'prediction':
        const predictionDisclaimer = $localize`:@@applovinPredictionDisclaimer:This prediction is for illustrative purposes only. Actual results vary based on individual effort, consistency, and usage patterns.`;
        return {
          ...data,
          title: $localize`:@@applovinPredictionTitle:We predict you could`,
          subtitle:
            $localize`:@@applovinPredictionSubtitle:be able to consume more content and retain more information in the next 3 months` +
            `<br/><br/><i class="fs-12 muted-color">${predictionDisclaimer}</i>`,
        };

      case 'amountInfo':
        const amountDisclaimer = $localize`:@@applovinAmountDisclaimer:*User count based on total app downloads. Individual experiences may vary.`;
        return {
          ...data,
          subtitle: `<i class="fs-12 muted-color">${amountDisclaimer}</i>`,
        };

      case 'retentionInfo':
        const retentionDisclaimer = $localize`:@@applovinRetentionDisclaimer:Individual results may vary. See study: Catalan & Marcelo, 2022, Lukad Journal of Pedagogy`;
        return {
          ...data,
          title: highlightText($localize`:@@didYouKnow:Did You Know?`),
          subtitle:
            $localize`:@@applovinRetentionSubtitle:${data.subtitle}:INTERPOLATION:` +
            `<br/><br/><i class="fs-12 muted-color">${retentionDisclaimer}</i>`,
        };

      case 'timeForReadingInfo':
        const timeReadingDisclaimer = $localize`:@@applovinTimeReadingDisclaimer:Usage statistics are aggregate estimates. Individual usage varies.`;
        return {
          ...data,
          title: $localize`:@@quiteCommon:It is quite common 🤓`,
          subtitle:
            $localize`:@@applovinTimeReadingSubtitle:${data.subtitle}:INTERPOLATION:` +
            `<br/><br/><i class="fs-12 muted-color">${timeReadingDisclaimer}</i>`,
        };

      default:
        return data;
    }
  }

  /**
   * Override to provide compliant goal info subtitle with research citation and disclaimer
   */
  override getGoalInfoSubtitle(): string {
    const base = super.getGoalInfoSubtitle();
    const disclaimer = $localize`:@@applovinGoalInfoDisclaimer:The graph is for demonstration purposes only. Your results may vary and depend on your efforts.`;
    return `${base}<br/><br/><i class="fs-12 muted-color">${disclaimer}</i>`;
  }
}
